import { DID_CREATE_PAYMENT, DID_FAIL_CREATING_PAYMENT, WILL_CREATE_PAYMENT } from '../constants/index';

import fetch from '../utils/fetch-json';

import { getFormActions } from '../utils/form-helpers';
import formConfig from './stripe-form-config';
import {
    getAnalyticsTrackingInfoForPayment,
    trackEvent,
    trackPixelEvent,
} from '../support-and-tracking/analytics-actions';
import { produceGlobalError } from '../utils/error';
import { CART_USER_TOKEN_IDENTIFIER } from '../cart/cart-actions';
import storage from '../utils/storage';

const { setErrors, setFormField } = getFormActions(formConfig);
const SessionStorage = storage('sessionStorage');

export const initiateStripePayment =
    ({
        projectId,
        amount,
        paymentMethod,
        isFinished,
        title,
        email,
        rewardGiverInfoMetadata,
        stripe,
        giverName,
        comment,
        rewards,
        subprojectId,
        projectPollOptionId,
        greetingCard,
        international,
    }) =>
    async ({ dispatch, getState }) => {
        dispatch(setFormField('isProcessingPayment', true));
        dispatch({ type: WILL_CREATE_PAYMENT });
        const state = getState();
        const stripeGiveApiUrl = state.baseURL.concat(`/api/public/stripe/${projectId}/give`);
        const stripeRequiresActionApiUrl = state.baseURL.concat(`/api/public/stripe/${projectId}/action-completed`);
        const analyticsTrackingInfo = await getAnalyticsTrackingInfoForPayment({ state, projectId });

        try {
            const response = await fetch({
                url: stripeGiveApiUrl,
                options: {
                    method: 'POST',
                    body: {
                        projectId,
                        title,
                        amount,
                        rewards,
                        paymentMethod,
                        rewardGiverInfoMetadata,
                        email,
                        nameAndComment: { giverName, comment, anonymous: giverName ? false : undefined },
                        subprojectId,
                        projectPollOptionId,
                        userTokenId: SessionStorage.get(CART_USER_TOKEN_IDENTIFIER),
                        greetingCard,
                        analyticsTrackingInfo,
                        international,
                    },
                },
                dispatch,
                getState,
            });

            return await dispatch(handleServerResponse({ stripeRequiresActionApiUrl, stripe, response, isFinished }));
        } catch (error) {
            handleError(error.body, dispatch, isFinished);
        }
    };

const handleServerResponse =
    ({ stripeRequiresActionApiUrl, stripe, response, isFinished }) =>
    async ({ dispatch, getState }) => {
        if (response.error) {
            dispatch(setFormField('requiresAction', false));
            return handleError(
                produceGlobalError('Kunne ikke gjennomføre betalingen, prøv igjen senere.'),
                dispatch,
                isFinished
            );
        } else if (response.body?.requires_action) {
            dispatch(setFormField('requiresAction', true));
            const result = await stripe.handleCardAction(response.body.payment_intent_client_secret);
            if (result.error) {
                dispatch(setFormField('requiresAction', false));
                return handleError(
                    produceGlobalError(
                        'Vi klarer ikke å se om du autentiserte betalingen eller ikke. Vi har ikke trukket penger fra kortet ditt!'
                    ),
                    dispatch,
                    isFinished
                );
            } else {
                try {
                    const confirmResult = await fetch({
                        url: stripeRequiresActionApiUrl,
                        options: {
                            method: 'POST',
                            body: { paymentIntentId: result.paymentIntent.id },
                        },
                        dispatch,
                        getState,
                    });
                    return dispatch(
                        handleServerResponse({
                            stripeRequiresActionApiUrl,
                            stripe,
                            response: confirmResult,
                            isFinished,
                        })
                    );
                } catch (error) {
                    dispatch(setFormField('requiresAction', false));
                    return handleError(error.body, dispatch, isFinished);
                }
            }
        } else {
            dispatch(setFormField('requiresAction', false));
            dispatch({ type: DID_CREATE_PAYMENT, data: response.body });
            isFinished({
                didGiveSupport: true,
                transactionForGiverInfo: getState().transactions.transactionForGiverInfo,
            });

            trackPixelEvent('Purchase', { value: response.body.transaction.amount, currency: 'NOK' });
            trackEvent('support', 'checkout-complete', {
                method: 'Stripe',
                'project-id': response.body.project.id,
                amount: response.body.transaction.amount,
            });
        }
    };

const handleError = (errorBody, dispatch, isFinished) => {
    dispatch(setErrors(errorBody?.errors));
    stopLoading(dispatch);
    isFinished();
    return dispatch({ type: DID_FAIL_CREATING_PAYMENT, errors: errorBody?.errors });
};

function stopLoading(dispatch) {
    dispatch(setFormField('isPerformingPayment', false));
    dispatch(setFormField('isProcessingPayment', false));
}
